import { Injectable, computed, inject, signal } from '@angular/core';
import { SignupRequirements } from '@models/signup-requirements.model';
import { HttpClientService } from '@services/http-client.service';
import { LoggerService } from '@services/logger.service';

/**
 * Loads the brand's public signup requirements once at bootstrap
 * (GET /api/v1/signup-policies/requirements) so the registration wizard can
 * adapt to the admin's policy. Currently used to restrict the phone-input
 * country list to `allowed_country_codes`.
 *
 * Fail-open: any error leaves the requirements unset, so the wizard behaves
 * exactly as it did before this fetch existed.
 */
@Injectable({ providedIn: 'root' })
export class SignupRequirementsService {

  private httpClientService = inject(HttpClientService);

  readonly requirements = signal<SignupRequirements | null>(null);

  /**
   * True once the requirements fetch has SETTLED (success OR failure). Lets the
   * wizard resolve the registration mode from the brand policy without hanging
   * on a fail-open fetch — on error we still mark it loaded and fall back to PLG.
   */
  readonly loaded = signal(false);

  /**
   * The brand's default registration mode, admin-set on the Signup Rules page
   * (`default_registration_mode`). This is the single source of truth for the
   * PLG/SLG wizard shape — the old `?mode=` query param is deprecated. Defaults
   * to PLG when the policy is unset or the fetch failed.
   */
  readonly registrationMode = computed<string>(() => this.requirements()?.registration_mode ?? 'PLG');

  /**
   * Countries the phone input may offer, as lowercase ISO-2 codes for
   * intl-tel-input. Empty means no restriction (offer every country).
   * Normalises the stored values (upper -> lower) and maps the non-ISO `UK`
   * to its real ISO code `GB`.
   */
  readonly allowedCountries = computed<string[]>(() =>
    (this.requirements()?.allowed_country_codes ?? [])
      .map(code => code.trim().toLowerCase())
      .map(code => (code === 'uk' ? 'gb' : code))
      .filter(Boolean)
  );

  /**
   * Whether the brand requires mobile verification before login (drives the
   * mobile-OTP step). Used to render the phone field in SLG mode, where the
   * form is otherwise email + password only. JB-1569.
   */
  readonly requireMobileConfirmation = computed<boolean>(() => !!this.requirements()?.require_mobile_confirmation);

  /**
   * Whether the brand requires email confirmation (email OTP) at signup. Used
   * with requireMobileConfirmation to compute the "x of y" progress cue on the OTP screens.
   * JB-1686.
   */
  readonly requireEmailConfirmation = computed<boolean>(() => !!this.requirements()?.require_email_confirmation);

  /**
   * Whether the brand still requires a support person to manually verify the new
   * partner before the account is fully usable. Drives the success-screen copy:
   * true -> "our support team will review your account"; false -> the account is
   * auto-approved and immediately usable. Defaults to true so an older backend
   * (or a failed/unset fetch) keeps the pre-existing "support will review" copy
   * — the account is only announced as ready when the policy explicitly says so.
   */
  readonly supportMustVerify = computed<boolean>(() => this.requirements()?.support_must_verify ?? true);

  load(): void {
    this.httpClientService.getV1('signup-policies/requirements')
      .subscribe({
        next: (requirements: SignupRequirements) => {
          this.requirements.set(requirements);
          this.loaded.set(true);
        },
        error: error => {
          LoggerService.error(error);
          this.loaded.set(true);
        }
      });
  }
}
